import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/useAuthStore";
import {
  completeMyOnboarding,
  createMyRadiologist,
  getAttestation,
  getIntakeRadiologists,
  getIntakeStatus,
  getITOnboarding,
  getMyRadiologists,
  getReferences,
  getSpecialties,
  updateLegalName,
} from "@/features/intake/api/intake";
import { previewCreateRad } from "@/features/intake/lib/intakeSandbox";
import {
  dbSubject,
  subjectKey,
  type IntakeSubject,
} from "@/features/intake/lib/intakeSubject";
import type { IntakeRadiologist } from "@/features/intake/types/intake";
import type { Radiologist } from "@/features/radiologists/types/radiologist";

const FIVE_MIN = 5 * 60 * 1000;
const ONE_MIN = 60 * 1000;

// Stable reference so consumers' useMemo/effect deps don't churn every render
// while the query has no data yet (e.g. loading or offline).
const EMPTY_RADS: Radiologist[] = [];
const EMPTY_ROSTER: IntakeRadiologist[] = [];

// Per-rad caches are keyed by `subjectKey`, not a raw id, so a radiologist
// addressed by their Drive folder gets its own cache entry.
export const intakeKeys = {
  myRadiologists: ["intake", "my-radiologists"] as const,
  radiologists: ["intake", "radiologists"] as const,
  status: (subject: IntakeSubject) => ["intake", "status", subjectKey(subject)] as const,
  statusDisabled: ["intake", "status", "disabled"] as const,
  attestation: (subject: IntakeSubject) =>
    ["intake", "attestation", subjectKey(subject)] as const,
  attestationDisabled: ["intake", "attestation", "disabled"] as const,
  references: (subject: IntakeSubject) => ["intake", "references", subjectKey(subject)] as const,
  referencesDisabled: ["intake", "references", "disabled"] as const,
  it: (subject: IntakeSubject) => ["intake", "it", subjectKey(subject)] as const,
  itDisabled: ["intake", "it", "disabled"] as const,
  specialties: (subject: IntakeSubject) => ["intake", "specialties", subjectKey(subject)] as const,
  specialtiesDisabled: ["intake", "specialties", "disabled"] as const,
};

/** Radiologists linked to the current account. */
export function useMyRadiologists(enabled = true) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const query = useQuery({
    queryKey: intakeKeys.myRadiologists,
    queryFn: ({ signal }) => getMyRadiologists(signal),
    enabled: isAuthenticated && enabled,
    staleTime: FIVE_MIN,
    retry: 1,
    refetchOnWindowFocus: false,
  });
  return {
    rads: query.data ?? EMPTY_RADS,
    isLoading: isAuthenticated && enabled && query.isLoading,
    isError: query.isError,
    isSuccess: query.isSuccess,
  };
}

/**
 * Every radiologist the Intake page can act on. For a manager that is the merged
 * roster — scheduler records *and* Drive intake folders — already sorted by last
 * then first name; for a radiologist it is just their own linked records.
 */
export function useIntakeRadiologists(enabled = true) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const query = useQuery({
    queryKey: intakeKeys.radiologists,
    queryFn: ({ signal }) => getIntakeRadiologists(signal),
    enabled: isAuthenticated && enabled,
    staleTime: FIVE_MIN,
    retry: 1,
    refetchOnWindowFocus: false,
  });
  return {
    rads: query.data ?? EMPTY_ROSTER,
    isLoading: isAuthenticated && enabled && query.isLoading,
    // True during a background refetch too, when `rads` is still the previous
    // roster. Callers that compare a selection against it need to know the list
    // they are looking at may be out of date.
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
  };
}

/**
 * Change a radiologist's full legal name (Drive folder + scheduler record +
 * checklist value). A rename can move the rad's cache identity, so the entries
 * under the old one are dropped and the rest are refreshed.
 */
export function useUpdateLegalName(subject: IntakeSubject | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: { firstName: string; middleName: string; lastName: string }) => {
      if (subject == null) return Promise.reject(new Error("No radiologist selected."));
      return updateLegalName(subject, name);
    },
    onSuccess: () => {
      // For a Drive-addressed radiologist the folder name *is* their cache
      // identity, so a rename strands every entry under the old key — entries
      // nothing will ask for again, and which a plain invalidation would refetch
      // against a folder that no longer exists. Drop those outright. A rad with a
      // scheduler record keeps their key across a rename, so invalidating is enough.
      if (subject != null && subject.radId == null) {
        const staleKey = subjectKey(subject);
        queryClient.removeQueries({
          predicate: (q) => q.queryKey[0] === "intake" && q.queryKey[2] === staleKey,
        });
      }
      // Refresh the rest — the roster and the caller's own radiologists both show
      // a different name now. Deliberately not returned: react-query awaits a
      // promise returned from onSuccess, which would keep the caller in its
      // saving state until every refetch had finished.
      void queryClient.invalidateQueries({ queryKey: ["intake"] });
    },
  });
}

/** Attestation questions + this radiologist's previously submitted answers. */
export function useAttestation(subject: IntakeSubject | null, enabled = true) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const shouldFetch = isAuthenticated && enabled && subject != null;
  return useQuery({
    queryKey: shouldFetch ? intakeKeys.attestation(subject) : intakeKeys.attestationDisabled,
    queryFn: ({ signal }) => getAttestation(subject!, signal),
    enabled: shouldFetch,
    staleTime: FIVE_MIN,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

/** This radiologist's saved professional references (5 slots). */
export function useReferences(subject: IntakeSubject | null, enabled = true) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const shouldFetch = isAuthenticated && enabled && subject != null;
  return useQuery({
    queryKey: shouldFetch ? intakeKeys.references(subject) : intakeKeys.referencesDisabled,
    queryFn: ({ signal }) => getReferences(subject!, signal),
    enabled: shouldFetch,
    staleTime: FIVE_MIN,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

/** IT onboarding options + this radiologist's chosen option. */
export function useITOnboarding(subject: IntakeSubject | null, enabled = true) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const shouldFetch = isAuthenticated && enabled && subject != null;
  return useQuery({
    queryKey: shouldFetch ? intakeKeys.it(subject) : intakeKeys.itDisabled,
    queryFn: ({ signal }) => getITOnboarding(subject!, signal),
    enabled: shouldFetch,
    staleTime: FIVE_MIN,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

/** Specialty options + this radiologist's selected specialties. */
export function useSpecialties(subject: IntakeSubject | null, enabled = true) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const shouldFetch = isAuthenticated && enabled && subject != null;
  return useQuery({
    queryKey: shouldFetch ? intakeKeys.specialties(subject) : intakeKeys.specialtiesDisabled,
    queryFn: ({ signal }) => getSpecialties(subject!, signal),
    enabled: shouldFetch,
    staleTime: FIVE_MIN,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

/**
 * Create + link the caller's own radiologist (Step 1 of the intake wizard).
 * In `preview` mode the radiologist is a throwaway sandbox record — there's no
 * rad id to route on yet, so the branch lives here rather than in the api layer.
 */
export function useCreateMyRadiologist(preview = false) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ firstName, lastName }: { firstName: string; lastName: string }) =>
      preview
        ? Promise.resolve(previewCreateRad(firstName, lastName))
        : createMyRadiologist(firstName, lastName),
    onSuccess: () => {
      if (preview) return;
      void queryClient.invalidateQueries({ queryKey: intakeKeys.myRadiologists });
    },
  });
}

/** Mark the walkthrough complete; flips the page to the full checklist. */
export function useCompleteOnboarding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (radId: number) => completeMyOnboarding(radId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: intakeKeys.myRadiologists }),
  });
}

/** Intake status (required vs uploaded) for one radiologist. */
export function useIntakeStatus(subject: IntakeSubject | null) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const shouldFetch = isAuthenticated && subject != null;
  return useQuery({
    queryKey: shouldFetch ? intakeKeys.status(subject) : intakeKeys.statusDisabled,
    queryFn: ({ signal }) => getIntakeStatus(subject!, signal),
    enabled: shouldFetch,
    staleTime: ONE_MIN,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

/**
 * Total missing documents across the given radiologists — powers the sidebar
 * red badge. Shares the per-rad `status` cache with the Intake page.
 */
export function useMyIntakeMissing(radIds: number[], enabled: boolean) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const results = useQueries({
    queries: radIds.map((id) => ({
      queryKey: intakeKeys.status(dbSubject(id)),
      queryFn: ({ signal }: { signal: AbortSignal }) => getIntakeStatus(dbSubject(id), signal),
      enabled: isAuthenticated && enabled,
      staleTime: ONE_MIN,
      retry: 1,
      refetchOnWindowFocus: false,
    })),
  });
  const isError = results.some((r) => r.isError);
  const isLoading = results.some((r) => r.isLoading);
  const missing = isError
    ? undefined
    : results.reduce((sum, r) => sum + (r.data?.missing_count ?? 0), 0);
  return { missing, isLoading, isError };
}
